// Config-calibration vs run-solve drift evaluation, shared by RunEntry (full
// mismatch details for the asterisk tooltip) and ConfigEntry (session-wide
// count driving the run-vs-session reprocess recommendation).
//
// Tolerance bands come from session.calibration_drift_bands (server
// WARNING_CONSTS + per-session session_health_vars override). Each band is
// [warn, error]; either stage may be null. Solver snapshot lives under
// solve.inputs.* (see SolveInputs in Run.ts).

import { isSyntheticDynamicPressure } from '../../../utils/effectiveCalibration';
import { CalibrationDriftBands } from 'types/Session';

export type DriftSeverity = 'green' | 'yellow' | 'red';

export interface DriftMismatch {
  field: string;
  configValue: any;
  runValue: any;
  severity: DriftSeverity;
}

export interface DriftStatus {
  worstSeverity: DriftSeverity;
  mismatches: DriftMismatch[];
}

export function checkValueMatches(
  run: any,
  config: any,
  driftBands?: CalibrationDriftBands,
): DriftStatus {
  const mismatches: DriftMismatch[] = [];
  let worstSeverity: DriftSeverity = 'green';

  const evalDrift = (
    field: string,
    configValue: number | undefined,
    runValue: number | null | undefined,
    band: [number | null, number | null] | undefined,
  ) => {
    if (configValue === undefined || runValue === undefined || runValue === null || !band) {
      return;
    }
    const [warn, error] = band;
    const diff = Math.abs(configValue - runValue);
    let severity: DriftSeverity = 'green';
    if (error != null && diff >= error) {
      severity = 'red';
    } else if (warn != null && diff >= warn) {
      severity = 'yellow';
    }
    if (severity !== 'green') {
      mismatches.push({ field, configValue, runValue, severity });
      if (severity === 'red' || (worstSeverity === 'green' && severity === 'yellow')) {
        worstSeverity = severity;
      }
    }
  };

  // Synthetic-DP runs have no probe, so the pitot-derived parameters are
  // meaningless there: the solve deliberately uses cp_corr = 1 and the yaw
  // offset has nothing to calibrate against. Comparing either against the
  // config value is not drift. Wheel circumference is probe-independent and
  // is always checked.
  if (!isSyntheticDynamicPressure(run)) {
    evalDrift('CP Correction', config?.cp_corr, run?.solve?.inputs?.cp_corr,
      driftBands?.cp_corr_diff);
    evalDrift('Yaw Offset', config?.yaw_offset, run?.solve?.inputs?.yaw_offset,
      driftBands?.yaw_offset_diff);
  }
  evalDrift('Wheel Circumference', config?.wheel_circumference,
    run?.solve?.inputs?.wheel_circumference, driftBands?.wheel_circumference_m_diff);

  return { worstSeverity, mismatches };
}
